import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Divider,
  Tab,
  Tabs,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DirectionsCar as CarIcon,
  Build as BuildIcon,
  Message as MessageIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  vin: string;
  registration: string;
  color: string;
  purchaseDate: Date;
  lastService: Date;
  nextService: Date;
}

interface ServiceHistory {
  id: string;
  date: Date;
  type: string;
  description: string;
  cost: number;
  status: string;
}

interface Communication {
  id: string;
  date: Date;
  type: 'email' | 'phone' | 'message';
  subject: string;
  content: string;
  status: 'sent' | 'received' | 'pending';
}

interface Customer {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  address: string;
  joinDate: Date;
  status: 'active' | 'inactive';
  type: 'vip' | 'premium' | 'standard';
  vehicles: Vehicle[];
  serviceHistory: ServiceHistory[];
  communications: Communication[];
  preferences: {
    preferredContactMethod: 'email' | 'phone' | 'message';
    serviceReminders: boolean;
    marketingCommunications: boolean;
    specialEvents: boolean;
    newsletter: boolean;
  };
  notes: string;
}

interface FilterForm {
  type: string[];
  status: string[];
  dateRange: {
    start: string;
    end: string;
  };
}

interface NewCustomerForm {
  title: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  type: string;
  vehicle: {
    make: string;
    model: string;
    year: number;
    vin: string;
    registration: string;
    color: string;
  };
}

export const CustomerManagement: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [openNewCustomerDialog, setOpenNewCustomerDialog] = useState(false);
  const [filters, setFilters] = useState<FilterForm>({
    type: [],
    status: [],
    dateRange: {
      start: '',
      end: '',
    },
  });
  const [newCustomer, setNewCustomer] = useState<NewCustomerForm>({
    title: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    type: 'standard',
    vehicle: {
      make: '',
      model: '',
      year: new Date().getFullYear(),
      vin: '',
      registration: '',
      color: '',
    },
  });

  // Mock customer data for luxury vehicle owners
  const customers: Customer[] = [
    {
      id: 'CUS001',
      name: 'James van der Merwe',
      title: 'Dr.',
      email: 'james.vdm@email.com',
      phone: '+27 82 123 4567',
      address: '123 Waterfront Drive, Cape Town, 8001',
      joinDate: new Date(2022, 5, 15),
      status: 'active',
      type: 'vip',
      vehicles: [
        {
          id: 'VEH001',
          make: 'Ferrari',
          model: 'SF90 Stradale',
          year: 2024,
          vin: 'ZFF92LMX000123456',
          registration: 'CT-SF90-24',
          color: 'Rosso Corsa',
          purchaseDate: new Date(2024, 0, 15),
          lastService: new Date(2024, 2, 10),
          nextService: new Date(2024, 8, 10),
        },
        {
          id: 'VEH002',
          make: 'Porsche',
          model: '911 GT3 RS',
          year: 2023,
          vin: 'WP0ZZZ99ZTS392045',
          registration: 'CT-GT3-23',
          color: 'GT Silver Metallic',
          purchaseDate: new Date(2023, 6, 1),
          lastService: new Date(2024, 1, 15),
          nextService: new Date(2024, 7, 15),
        },
      ],
      serviceHistory: [
        {
          id: 'SRV001',
          date: new Date(2024, 2, 10),
          type: 'Annual Service',
          description: 'Full annual service with brake system upgrade',
          cost: 185000,
          status: 'completed',
        },
        {
          id: 'SRV002',
          date: new Date(2024, 1, 15),
          type: 'Maintenance',
          description: 'Regular maintenance and software updates',
          cost: 45000,
          status: 'completed',
        },
      ],
      communications: [
        {
          id: 'COM001',
          date: new Date(2024, 2, 15),
          type: 'email',
          subject: 'Ferrari Track Day Invitation',
          content: 'Exclusive invitation to Ferrari owners track day event',
          status: 'sent',
        },
        {
          id: 'COM002',
          date: new Date(2024, 2, 12),
          type: 'phone',
          subject: 'Service Follow-up',
          content: 'Follow-up call regarding recent service experience',
          status: 'received',
        },
      ],
      preferences: {
        preferredContactMethod: 'email',
        serviceReminders: true,
        marketingCommunications: true,
        specialEvents: true,
        newsletter: true,
      },
      notes: 'VIP client with multiple high-performance vehicles. Prefers early morning appointments.',
    },
    {
      id: 'CUS002',
      name: 'Sarah Thompson',
      title: 'Mrs.',
      email: 'sarah.t@email.com',
      phone: '+27 83 234 5678',
      address: '456 Summit Road, Johannesburg, 2196',
      joinDate: new Date(2023, 3, 20),
      status: 'active',
      type: 'premium',
      vehicles: [
        {
          id: 'VEH003',
          make: 'McLaren',
          model: '720S',
          year: 2023,
          vin: 'SBM14DCA4NW123456',
          registration: 'GP-720S-23',
          color: 'Papaya Spark',
          purchaseDate: new Date(2023, 3, 20),
          lastService: new Date(2024, 1, 5),
          nextService: new Date(2024, 7, 5),
        },
      ],
      serviceHistory: [
        {
          id: 'SRV003',
          date: new Date(2024, 1, 5),
          type: 'Scheduled Service',
          description: 'Regular maintenance and performance optimization',
          cost: 75000,
          status: 'completed',
        },
      ],
      communications: [
        {
          id: 'COM003',
          date: new Date(2024, 2, 10),
          type: 'message',
          subject: 'Service Appointment',
          content: 'Confirmation of next service appointment',
          status: 'sent',
        },
      ],
      preferences: {
        preferredContactMethod: 'message',
        serviceReminders: true,
        marketingCommunications: false,
        specialEvents: true,
        newsletter: false,
      },
      notes: 'Prefers weekend appointments. Interested in track events.',
    },
  ];

  const getCustomerTypeColor = (type: string) => {
    switch (type) {
      case 'vip':
        return 'error';
      case 'premium':
        return 'warning';
      case 'standard':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return `R ${amount.toLocaleString()}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setOpenDialog(true);
  };

  const handleFilterClick = () => {
    setOpenFilterDialog(true);
  };

  const handleFilterApply = () => {
    // Here you would typically filter the customers based on the selected filters
    setOpenFilterDialog(false);
  };

  const handleNewCustomerClick = () => {
    setOpenNewCustomerDialog(true);
  };

  const handleNewCustomerSubmit = () => {
    // Here you would typically make an API call to create the customer
    setOpenNewCustomerDialog(false);
    // Reset form
    setNewCustomer({
      title: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      type: 'standard',
      vehicle: {
        make: '',
        model: '',
        year: new Date().getFullYear(),
        vin: '',
        registration: '',
        color: '',
      },
    });
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Customer Management
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Total Customers
                </Typography>
                <Typography variant="h4">
                  {customers.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active accounts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  VIP Clients
                </Typography>
                <Typography variant="h4">
                  {customers.filter(c => c.type === 'vip').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Premium members
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Total Vehicles
                </Typography>
                <Typography variant="h4">
                  {customers.reduce((sum, c) => sum + c.vehicles.length, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Registered vehicles
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Service Revenue
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(
                    customers.reduce((sum, c) => 
                      sum + c.serviceHistory.reduce((total, s) => total + s.cost, 0), 0
                    )
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total revenue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search customers..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={handleFilterClick}
                >
                  Filter
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewCustomerClick}
                >
                  New Customer
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer Details</TableCell>
                <TableCell>Vehicles</TableCell>
                <TableCell>Status & Type</TableCell>
                <TableCell>Last Service</TableCell>
                <TableCell>Recent Communication</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {customer.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          {customer.title} {customer.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {customer.email}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {customer.phone}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      {customer.vehicles.map((vehicle) => (
                        <Typography key={vehicle.id} variant="body2">
                          {vehicle.make} {vehicle.model} ({vehicle.year})
                          <Typography variant="caption" display="block" color="text.secondary">
                            {vehicle.registration}
                          </Typography>
                        </Typography>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      <Chip
                        label={customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                        color={customer.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                      <Chip
                        label={customer.type.toUpperCase()}
                        color={getCustomerTypeColor(customer.type)}
                        size="small"
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {customer.serviceHistory.length > 0 ? (
                      <>
                        <Typography variant="body2">
                          {formatDate(customer.serviceHistory[0].date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {customer.serviceHistory[0].type}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No service history
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {customer.communications.length > 0 ? (
                      <>
                        <Typography variant="body2">
                          {formatDate(customer.communications[0].date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {customer.communications[0].subject}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No recent communications
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewCustomer(customer)}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Customer Profile
          </DialogTitle>
          <DialogContent>
            {selectedCustomer && (
              <Box sx={{ mt: 2 }}>
                <Tabs
                  value={currentTab}
                  onChange={(_, newValue) => setCurrentTab(newValue)}
                  sx={{ mb: 2 }}
                >
                  <Tab label="Overview" />
                  <Tab label="Vehicles" />
                  <Tab label="Service History" />
                  <Tab label="Communications" />
                </Tabs>

                {currentTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Personal Information
                      </Typography>
                      <Typography variant="body2">
                        Name: {selectedCustomer.title} {selectedCustomer.name}
                      </Typography>
                      <Typography variant="body2">
                        Email: {selectedCustomer.email}
                      </Typography>
                      <Typography variant="body2">
                        Phone: {selectedCustomer.phone}
                      </Typography>
                      <Typography variant="body2">
                        Address: {selectedCustomer.address}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Account Information
                      </Typography>
                      <Typography variant="body2">
                        Customer ID: {selectedCustomer.id}
                      </Typography>
                      <Typography variant="body2">
                        Join Date: {formatDate(selectedCustomer.joinDate)}
                      </Typography>
                      <Typography variant="body2">
                        Status: {selectedCustomer.status.charAt(0).toUpperCase() + selectedCustomer.status.slice(1)}
                      </Typography>
                      <Typography variant="body2">
                        Type: {selectedCustomer.type.toUpperCase()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Preferences
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            Preferred Contact: {selectedCustomer.preferences.preferredContactMethod}
                          </Typography>
                          <Typography variant="body2">
                            Service Reminders: {selectedCustomer.preferences.serviceReminders ? 'Yes' : 'No'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            Marketing: {selectedCustomer.preferences.marketingCommunications ? 'Yes' : 'No'}
                          </Typography>
                          <Typography variant="body2">
                            Special Events: {selectedCustomer.preferences.specialEvents ? 'Yes' : 'No'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {selectedCustomer.notes}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {currentTab === 1 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Vehicle Details</TableCell>
                          <TableCell>Registration</TableCell>
                          <TableCell>Purchase Date</TableCell>
                          <TableCell>Service Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedCustomer.vehicles.map((vehicle) => (
                          <TableRow key={vehicle.id}>
                            <TableCell>
                              <Typography variant="body2">
                                {vehicle.make} {vehicle.model}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {vehicle.year} - {vehicle.color}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                VIN: {vehicle.vin}
                              </Typography>
                            </TableCell>
                            <TableCell>{vehicle.registration}</TableCell>
                            <TableCell>{formatDate(vehicle.purchaseDate)}</TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                Last Service: {formatDate(vehicle.lastService)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Next Service: {formatDate(vehicle.nextService)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {currentTab === 2 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Service Type</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Cost</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedCustomer.serviceHistory.map((service) => (
                          <TableRow key={service.id}>
                            <TableCell>{formatDate(service.date)}</TableCell>
                            <TableCell>{service.type}</TableCell>
                            <TableCell>{service.description}</TableCell>
                            <TableCell>{formatCurrency(service.cost)}</TableCell>
                            <TableCell>
                              <Chip
                                label={service.status}
                                color={service.status === 'completed' ? 'success' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {currentTab === 3 && (
                  <List>
                    {selectedCustomer.communications.map((comm) => (
                      <ListItem key={comm.id}>
                        <ListItemAvatar>
                          <Avatar>
                            {comm.type === 'email' ? <EmailIcon /> : 
                             comm.type === 'phone' ? <PhoneIcon /> : 
                             <MessageIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={comm.subject}
                          secondary={
                            <>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(comm.date)}
                              </Typography>
                              <Typography variant="body2">
                                {comm.content}
                              </Typography>
                            </>
                          }
                        />
                        <Chip
                          label={comm.status}
                          color={comm.status === 'sent' ? 'success' : 
                                comm.status === 'received' ? 'info' : 
                                'warning'}
                          size="small"
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Close</Button>
            <Button variant="contained" color="primary">
              Update Profile
            </Button>
          </DialogActions>
        </Dialog>

        {/* Filter Dialog */}
        <Dialog
          open={openFilterDialog}
          onClose={() => setOpenFilterDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Filter Customers</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" gutterBottom>
                    Customer Type
                  </Typography>
                  <FormGroup>
                    {['standard', 'premium', 'vip'].map((type) => (
                      <FormControlLabel
                        key={type}
                        control={
                          <Checkbox
                            checked={filters.type.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters({
                                  ...filters,
                                  type: [...filters.type, type],
                                });
                              } else {
                                setFilters({
                                  ...filters,
                                  type: filters.type.filter((t) => t !== type),
                                });
                              }
                            }}
                          />
                        }
                        label={type.charAt(0).toUpperCase() + type.slice(1)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" gutterBottom>
                    Status
                  </Typography>
                  <FormGroup>
                    {['active', 'inactive'].map((status) => (
                      <FormControlLabel
                        key={status}
                        control={
                          <Checkbox
                            checked={filters.status.includes(status)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters({
                                  ...filters,
                                  status: [...filters.status, status],
                                });
                              } else {
                                setFilters({
                                  ...filters,
                                  status: filters.status.filter((s) => s !== status),
                                });
                              }
                            }}
                          />
                        }
                        label={status.charAt(0).toUpperCase() + status.slice(1)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, end: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenFilterDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleFilterApply}>
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Customer Dialog */}
        <Dialog
          open={openNewCustomerDialog}
          onClose={() => setOpenNewCustomerDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Add New Customer</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Title</InputLabel>
                  <Select
                    value={newCustomer.title}
                    label="Title"
                    onChange={(e) => setNewCustomer({ ...newCustomer, title: e.target.value })}
                  >
                    <MenuItem value="Mr">Mr</MenuItem>
                    <MenuItem value="Mrs">Mrs</MenuItem>
                    <MenuItem value="Ms">Ms</MenuItem>
                    <MenuItem value="Dr">Dr</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={10}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Customer Type</InputLabel>
                  <Select
                    value={newCustomer.type}
                    label="Customer Type"
                    onChange={(e) => setNewCustomer({ ...newCustomer, type: e.target.value })}
                  >
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="premium">Premium</MenuItem>
                    <MenuItem value="vip">VIP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Vehicle Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Make"
                  value={newCustomer.vehicle.make}
                  onChange={(e) => setNewCustomer({
                    ...newCustomer,
                    vehicle: { ...newCustomer.vehicle, make: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Model"
                  value={newCustomer.vehicle.model}
                  onChange={(e) => setNewCustomer({
                    ...newCustomer,
                    vehicle: { ...newCustomer.vehicle, model: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={newCustomer.vehicle.year}
                  onChange={(e) => setNewCustomer({
                    ...newCustomer,
                    vehicle: { ...newCustomer.vehicle, year: parseInt(e.target.value) || new Date().getFullYear() }
                  })}
                  InputProps={{ inputProps: { min: 1900, max: new Date().getFullYear() + 1 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="VIN"
                  value={newCustomer.vehicle.vin}
                  onChange={(e) => setNewCustomer({
                    ...newCustomer,
                    vehicle: { ...newCustomer.vehicle, vin: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Registration"
                  value={newCustomer.vehicle.registration}
                  onChange={(e) => setNewCustomer({
                    ...newCustomer,
                    vehicle: { ...newCustomer.vehicle, registration: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Color"
                  value={newCustomer.vehicle.color}
                  onChange={(e) => setNewCustomer({
                    ...newCustomer,
                    vehicle: { ...newCustomer.vehicle, color: e.target.value }
                  })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNewCustomerDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleNewCustomerSubmit}
              disabled={!newCustomer.name || !newCustomer.email || !newCustomer.phone}
            >
              Add Customer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 